import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import cn from 'classnames';

const PURCHASE_STEPS = [
  {
    id: 1,
    phase: 'Avant-contrat',
    label: 'Compromis de vente',
    icon: 'FileSignature',
    duree: '1 à 2 semaines',
    color: 'blue',
    description: 'Accord préliminaire signé entre acheteur et vendeur fixant les conditions de la vente.',
    documents: [
      'CNI / Passeport des deux parties',
      'Titre foncier du vendeur',
      'Plan de situation du bien',
      'Attestation de non-litige',
    ],
    administration: 'Notaire agréé',
    cout: '50 000 – 150 000 FCFA (honoraires notaire)',
    conseil: 'Vérifiez l\'authenticité du titre foncier auprès de la Conservation Foncière avant signature.',
  },
  {
    id: 2,
    phase: 'Vérification',
    label: 'Due diligence foncière',
    icon: 'SearchCheck',
    duree: '2 à 4 semaines',
    color: 'amber',
    description: 'Vérification juridique et technique du bien : absence d\'hypothèque, de litige ou d\'irrégularité.',
    documents: [
      'Titre foncier original',
      'Certificat de non-hypothèque (Conservation Foncière)',
      'Certificat de non-litige (chef de village / mairie)',
      'Relevé cadastral',
    ],
    administration: 'Conservation Foncière — Plateau, Abidjan',
    cout: '25 000 – 75 000 FCFA',
    conseil: 'Le certificat de non-hypothèque est obligatoire. Toute hypothèque non levée bloque la mutation.',
  },
  {
    id: 3,
    phase: 'Financement',
    label: 'Obtention du crédit immobilier',
    icon: 'Landmark',
    duree: '3 à 8 semaines',
    color: 'violet',
    description: 'Dépôt du dossier de prêt auprès d\'une banque ou d\'un organisme de crédit agréé.',
    documents: [
      'Bulletins de salaire (3 derniers mois)',
      'Relevés bancaires (6 derniers mois)',
      'Compromis de vente signé',
      'Justificatif de domicile',
      'Attestation d\'emploi',
    ],
    administration: 'Banque / Établissement de crédit agréé BCEAO',
    cout: 'Frais de dossier : 0,5 % – 1 % du montant emprunté',
    conseil: 'Comparez les taux des banques locales (SIB, SGBCI, BNI, Ecobank). Le taux moyen est de 8 à 12 % en Côte d\'Ivoire.',
  },
  {
    id: 4,
    phase: 'Acte notarié',
    label: 'Signature de l\'acte de vente',
    icon: 'PenLine',
    duree: '1 à 2 semaines',
    color: 'emerald',
    description: 'Signature officielle de l\'acte authentique de vente devant notaire, transfert de propriété.',
    documents: [
      'CNI des deux parties (originaux)',
      'Titre foncier original',
      'Compromis de vente',
      'Accord de financement bancaire',
      'Quittance de paiement du prix de vente',
    ],
    administration: 'Chambre des Notaires de Côte d\'Ivoire',
    cout: '2 % – 5 % de la valeur du bien (émoluments + taxes)',
    conseil: 'L\'acte notarié est la seule preuve légale du transfert de propriété. Ne jamais signer un acte sous seing privé.',
  },
  {
    id: 5,
    phase: 'Enregistrement',
    label: 'Enregistrement fiscal de l\'acte',
    icon: 'Receipt',
    duree: '1 à 3 semaines',
    color: 'orange',
    description: 'Enregistrement de l\'acte de vente à la Direction Générale des Impôts pour acquitter les droits de mutation.',
    documents: [
      'Acte de vente notarié (original + 2 copies)',
      'CNI des parties',
      'Titre foncier',
      'Formulaire de déclaration de mutation (DGI)',
    ],
    administration: 'Direction Générale des Impôts (DGI) — www.dgi.gouv.ci',
    cout: '4 % – 10 % de la valeur vénale du bien (droits de mutation)',
    conseil: 'L\'enregistrement doit être effectué dans le mois suivant la signature. Tout retard entraîne des pénalités.',
  },
  {
    id: 6,
    phase: 'Mutation',
    label: 'Mutation du titre foncier',
    icon: 'RefreshCw',
    duree: '1 à 3 mois',
    color: 'teal',
    description: 'Transfert officiel du titre foncier au nom du nouvel acquéreur à la Conservation Foncière.',
    documents: [
      'Acte de vente enregistré à la DGI',
      'Titre foncier original du vendeur',
      'Quittance des droits de mutation payés',
      'CNI de l\'acheteur',
      'Demande de mutation (formulaire Conservation Foncière)',
    ],
    administration: 'Conservation Foncière — Ministère de la Construction',
    cout: 'Inclus dans les droits de mutation (DGI)',
    conseil: 'La mutation finalise juridiquement votre propriété. Sans elle, le titre reste au nom du vendeur.',
  },
];

const COLOR_MAP = {
  blue:   { bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-600 dark:text-blue-400',   border: 'border-blue-200 dark:border-blue-800/40',   dot: 'bg-blue-500',   ring: 'ring-blue-200 dark:ring-blue-800/40',   badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
  amber:  { bg: 'bg-amber-50 dark:bg-amber-900/20',  text: 'text-amber-600 dark:text-amber-400',  border: 'border-amber-200 dark:border-amber-800/40',  dot: 'bg-amber-500',  ring: 'ring-amber-200 dark:ring-amber-800/40',  badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' },
  violet: { bg: 'bg-violet-50 dark:bg-violet-900/20',text: 'text-violet-600 dark:text-violet-400',border: 'border-violet-200 dark:border-violet-800/40',dot: 'bg-violet-500', ring: 'ring-violet-200 dark:ring-violet-800/40', badge: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300' },
  emerald:{ bg: 'bg-emerald-50 dark:bg-emerald-900/20',text: 'text-emerald-600 dark:text-emerald-400',border: 'border-emerald-200 dark:border-emerald-800/40',dot: 'bg-emerald-500',ring: 'ring-emerald-200 dark:ring-emerald-800/40',badge: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-900/20',text: 'text-orange-600 dark:text-orange-400',border: 'border-orange-200 dark:border-orange-800/40',dot: 'bg-orange-500', ring: 'ring-orange-200 dark:ring-orange-800/40', badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
  teal:   { bg: 'bg-teal-50 dark:bg-teal-900/20',   text: 'text-teal-600 dark:text-teal-400',   border: 'border-teal-200 dark:border-teal-800/40',   dot: 'bg-teal-500',   ring: 'ring-teal-200 dark:ring-teal-800/40',   badge: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300' },
};

const StepCard = ({ step, index, isActive, onClick, isCompleted }) => {
  const c = COLOR_MAP?.[step?.color];
  return (
    <button
      onClick={() => onClick(step?.id)}
      className={cn(
        'w-full text-left rounded-xl border-2 p-4 transition-all duration-200 group relative overflow-hidden',
        isActive
          ? cn('shadow-md', c?.border, c?.bg)
          : isCompleted
          ? 'border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-900/10' :'border-border bg-card hover:border-brand-earth/30 hover:shadow-sm'
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          'flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all',
          isActive ? cn(c?.bg, 'ring-2', c?.ring) : isCompleted ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-accent'
        )}>
          {isCompleted
            ? <Icon name="CheckCircle2" size={18} strokeWidth={2} className="text-emerald-600 dark:text-emerald-400" />
            : <Icon name={step?.icon} size={18} strokeWidth={1.75} className={isActive ? c?.text : 'text-muted-foreground group-hover:text-brand-earth transition-colors'} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={cn('text-[10px] font-bold uppercase tracking-wider', isActive ? c?.text : 'text-muted-foreground')}>
              Étape {index + 1} · {step?.phase}
            </span>
          </div>
          <p className={cn('text-sm font-semibold leading-snug', isActive ? 'text-foreground' : 'text-foreground/80')}>
            {step?.label}
          </p>
          <div className="flex items-center gap-1 mt-1.5">
            <Icon name="Clock" size={11} strokeWidth={2} className="text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">{step?.duree}</span>
          </div>
        </div>
        <Icon
          name="ChevronRight"
          size={14}
          strokeWidth={2}
          className={cn('flex-shrink-0 mt-1 transition-transform', isActive ? cn(c?.text, 'rotate-90') : 'text-muted-foreground/40 group-hover:text-muted-foreground')}
        />
      </div>
    </button>
  );
};

const StepDetail = ({ step }) => {
  if (!step) return null;
  const c = COLOR_MAP?.[step?.color];

  return (
    <div className={cn('rounded-2xl border-2 overflow-hidden', c?.border)}>
      {/* Header */}
      <div className={cn('p-5 border-b', c?.bg, c?.border)}>
        <div className="flex items-center gap-3 mb-3">
          <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center ring-2', c?.bg, c?.ring)}>
            <Icon name={step?.icon} size={22} strokeWidth={1.75} className={c?.text} />
          </div>
          <div>
            <span className={cn('text-[10px] font-bold uppercase tracking-widest', c?.text)}>
              Étape {step?.id} · {step?.phase}
            </span>
            <h3 className="font-bold text-foreground text-base leading-tight">{step?.label}</h3>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{step?.description}</p>
      </div>
      <div className="p-5 space-y-5 bg-card">
        {/* Timeline + Cost */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-accent/50 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="Clock" size={14} strokeWidth={2} className={c?.text} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Délai estimé</span>
            </div>
            <p className="text-sm font-semibold text-foreground">{step?.duree}</p>
          </div>
          <div className="bg-accent/50 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="Banknote" size={14} strokeWidth={2} className={c?.text} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Coût estimé</span>
            </div>
            <p className="text-sm font-semibold text-foreground">{step?.cout}</p>
          </div>
        </div>

        {/* Administration */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Building2" size={14} strokeWidth={2} className={c?.text} />
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Administration compétente</span>
          </div>
          <p className="text-sm text-foreground font-medium">{step?.administration}</p>
        </div>

        {/* Documents */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="FolderOpen" size={14} strokeWidth={2} className={c?.text} />
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Documents requis</span>
          </div>
          <div className="space-y-2">
            {step?.documents?.map((doc, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className={cn('flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center mt-0.5', c?.bg)}>
                  <Icon name="FileCheck" size={11} strokeWidth={2.5} className={c?.text} />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{doc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Conseil */}
        <div className={cn('rounded-xl p-4 border', c?.bg, c?.border)}>
          <div className="flex items-start gap-2.5">
            <Icon name="Lightbulb" size={15} strokeWidth={2} className={cn('flex-shrink-0 mt-0.5', c?.text)} />
            <div>
              <p className={cn('text-[10px] font-bold uppercase tracking-wider mb-1', c?.text)}>Conseil pratique</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{step?.conseil}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TOTAL_DUREE = '4 à 7 mois';
const TOTAL_COUT = '8 % – 18 % de la valeur du bien';

const PropertyPurchaseGuide = ({ propertyContext }) => {
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isExpanded, setIsExpanded] = useState(true);

  const handleStepClick = (id) => {
    setActiveStep(prev => prev === id ? null : id);
  };

  const toggleCompleted = (id, e) => {
    e?.stopPropagation();
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next?.has(id) ? next?.delete(id) : next?.add(id);
      return next;
    });
  };

  const selectedStep = PURCHASE_STEPS?.find(s => s?.id === activeStep);
  const progress = Math.round((completedSteps?.size / PURCHASE_STEPS?.length) * 100);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
      {/* Guide Header */}
      <button
        onClick={() => setIsExpanded(p => !p)}
        className="w-full text-left"
      >
        <div className="bg-gradient-to-r from-brand-earth/10 via-brand-earth/5 to-transparent border-b border-border px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-earth/15 flex items-center justify-center flex-shrink-0">
              <Icon name="Home" size={20} strokeWidth={1.75} className="text-brand-earth" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-foreground text-base">Guide d'Achat Immobilier</h2>
                <span className="text-[10px] font-semibold bg-brand-earth/10 text-brand-earth px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Procédures liées
                </span>
                {propertyContext && (
                  <span className="text-[10px] font-semibold bg-accent text-muted-foreground px-2 py-0.5 rounded-full truncate max-w-[180px]">
                    {propertyContext}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Toutes les étapes administratives pour finaliser votre acquisition · {TOTAL_DUREE} · {TOTAL_COUT}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Progress ring */}
            <div className="hidden sm:flex items-center gap-2">
              <div className="relative w-9 h-9">
                <svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3" className="text-border" />
                  <circle
                    cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3"
                    strokeDasharray={`${progress * 0.942} 94.2`}
                    strokeLinecap="round"
                    className="text-brand-earth transition-all duration-500"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-brand-earth">{progress}%</span>
              </div>
              <span className="text-xs text-muted-foreground">{completedSteps?.size}/{PURCHASE_STEPS?.length}</span>
            </div>
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} strokeWidth={2} className="text-muted-foreground" />
          </div>
        </div>
      </button>
      {isExpanded && (
        <div className="p-5">
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-accent/50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-foreground">{PURCHASE_STEPS?.length}</p>
              <p className="text-[11px] text-muted-foreground">Étapes clés</p>
            </div>
            <div className="bg-accent/50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-foreground">4–7</p>
              <p className="text-[11px] text-muted-foreground">Mois estimés</p>
            </div>
            <div className="bg-accent/50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-foreground">8–18%</p>
              <p className="text-[11px] text-muted-foreground">Frais annexes</p>
            </div>
          </div>

          {/* Progress bar */}
          {completedSteps?.size > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-foreground">Progression de votre dossier</span>
                <span className="text-xs text-muted-foreground">{completedSteps?.size} étape{completedSteps?.size > 1 ? 's' : ''} complétée{completedSteps?.size > 1 ? 's' : ''}</span>
              </div>
              <div className="h-2 bg-accent rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-earth rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Main layout */}
          <div className="grid lg:grid-cols-[280px_1fr] gap-5">
            {/* Steps list */}
            <div className="space-y-2">
              {PURCHASE_STEPS?.map((step, i) => (
                <div key={step?.id} className="relative">
                  <StepCard
                    step={step}
                    index={i}
                    isActive={activeStep === step?.id}
                    onClick={handleStepClick}
                    isCompleted={completedSteps?.has(step?.id)}
                  />
                  {/* Mark complete button */}
                  <button
                    onClick={(e) => toggleCompleted(step?.id, e)}
                    className={cn(
                      'absolute top-3 right-10 text-[10px] font-semibold px-2 py-0.5 rounded-full transition-all',
                      completedSteps?.has(step?.id)
                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200' :'bg-accent text-muted-foreground hover:bg-accent/80 opacity-0 group-hover:opacity-100'
                    )}
                    title={completedSteps?.has(step?.id) ? 'Marquer comme non complété' : 'Marquer comme complété'}
                  >
                    {completedSteps?.has(step?.id) ? '✓ Fait' : '+ Fait'}
                  </button>
                </div>
              ))}

              {/* Connector line hint */}
              <div className="flex items-center gap-2 pt-1 px-1">
                <Icon name="Info" size={12} strokeWidth={2} className="text-muted-foreground/60 flex-shrink-0" />
                <p className="text-[11px] text-muted-foreground/60 leading-relaxed">
                  Cliquez sur une étape pour voir les détails. Marquez-la comme "Fait" pour suivre votre avancement.
                </p>
              </div>
            </div>

            {/* Step detail */}
            <div>
              {selectedStep ? (
                <StepDetail step={selectedStep} />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-12 bg-accent/30 rounded-2xl border border-dashed border-border">
                  <div className="w-12 h-12 rounded-2xl bg-brand-earth/10 flex items-center justify-center mb-3">
                    <Icon name="MousePointerClick" size={22} strokeWidth={1.5} className="text-brand-earth" />
                  </div>
                  <p className="font-semibold text-foreground mb-1">Sélectionnez une étape</p>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    Cliquez sur une étape à gauche pour afficher les documents requis, délais et conseils pratiques.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Bottom disclaimer */}
          <div className="mt-5 flex items-start gap-3 bg-brand-earth/5 border border-brand-earth/15 rounded-xl p-4">
            <Icon name="ShieldCheck" size={16} strokeWidth={1.75} className="text-brand-earth flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Recommandation :</span> Faites-vous accompagner par un notaire agréé et un avocat spécialisé en droit immobilier ivoirien pour sécuriser votre acquisition. Les délais et coûts sont indicatifs et peuvent varier selon la commune et la complexité du dossier.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyPurchaseGuide;
